/**
 * Audit log plateforme — vue cross-tenant des actions hotline One For All Distribution.
 *
 * Listing paginé, filtres par action et plage de dates. Lecture seule
 * (journal immutable côté BDD par convention applicative).
 *
 * Refonte R10 (mai 2026) :
 *   - En-tête avec LogoMark Radio accent urgent (cohérent mode hotline)
 *   - Alert info sticky "consultation journalisée platform.audit.read"
 *   - Card filtres glass form grid 4 cols (action / du / au / bouton)
 *   - Card table glass dense 5 colonnes (Date / Action / Opérateur / Tenant ciblé / IP)
 *   - Badge urgent pour signaler que c'est l'audit de la plateforme One For All Distribution
 *
 * Logique métier 100% préservée :
 *   - api.platform.auditLog paginé (PAGE_SIZE 50) avec filtres action/from/to
 *   - 5 actions : platform.tenant.list/read/update + platform.impersonate.start + platform.audit.read
 *   - applyFilters reset page 1 + refetch
 *   - Conversion datetime-local → ISO
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Input, Field, Alert, Badge, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { ChevronLeft, ChevronRight, Radio, AlertCircle } from 'lucide-react';
import type { PlatformAuditLogItem } from '@roronoa-collectivites/shared-types';

const PAGE_SIZE = 50;

const ACTION_LABEL: Record<string, string> = {
  'platform.tenant.list':         'Liste collectivités',
  'platform.tenant.read':         'Lecture détail tenant',
  'platform.tenant.update':       'Modification tenant',
  'platform.impersonate.start':   'Démarrage session hotline',
  'platform.audit.read':          'Consultation audit plateforme',
};

const COMMON_ACTIONS = [
  'platform.tenant.list',
  'platform.tenant.read',
  'platform.tenant.update',
  'platform.impersonate.start',
  'platform.audit.read',
];

const SELECT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-urgent focus:outline-none focus:ring-2 focus:ring-urgent focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

export function PlatformAuditLogPage() {
  const [items, setItems] = useState<PlatformAuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actionFilter, setActionFilter] = useState<string>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.platform.auditLog({
        page,
        pageSize: PAGE_SIZE,
        action: actionFilter === 'all' ? undefined : actionFilter,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      if (err instanceof ApiException) setError(err.payload.error || 'Erreur de chargement');
      else setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, from, to]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function applyFilters(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    fetchLog();
  }

  return (
    <section aria-labelledby="paudit-title" className="animate-fade-in">
      {/* En-tête : LogoMark Radio accent urgent (cohérent mode hotline) */}
      <header className="mb-4 flex items-center gap-3">
        <LogoMark icon={Radio} accent="urgent" size="lg" />
        <div>
          <SectionEyebrow as="div">Plateforme One For All Distribution</SectionEyebrow>
          <h1
            id="paudit-title"
            className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl"
          >
            Audit plateforme
          </h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            Journal immutable des actions cross-tenant menées par les opérateurs One For All Distribution. {total}{' '}
            entrée{total > 1 ? 's' : ''}.
          </p>
        </div>
      </header>

      <Alert variant="info" icon={Radio} className="mb-4">
        Chaque consultation de cette page est elle-même journalisée (action{' '}
        <code className="rounded-r-md bg-surface/60 px-1 py-0.5 font-mono text-xs">platform.audit.read</code>).
      </Alert>

      {/* Filtres en Card glass */}
      <Card variant="glass" padded className="mb-4">
        <form
          onSubmit={applyFilters}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          <Field label="Action" htmlFor="p-action">
            <select
              id="p-action"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className={SELECT_CLS}
            >
              <option value="all">Toutes les actions</option>
              {COMMON_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {ACTION_LABEL[a] ?? a}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Du" htmlFor="p-from">
            <Input
              id="p-from"
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Field>
          <Field label="Au" htmlFor="p-to">
            <Input
              id="p-to"
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Field>
          <div className="flex items-end">
            <Button type="submit" variant="primary" className="w-full">
              Filtrer
            </Button>
          </div>
        </form>
      </Card>

      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-4">
          {error}
        </Alert>
      )}

      <Card variant="glass" padded={false}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-border bg-surface/40 backdrop-blur-glass">
              <tr className="text-left">
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Date</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Action</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Opérateur</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Tenant ciblé</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">IP</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-ink-muted">
                    <div
                      className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-urgent border-t-transparent"
                      role="status"
                      aria-label="Chargement"
                    />
                    <p className="mt-3">Chargement…</p>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-ink-muted">
                    Aucune entrée ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-border last:border-b-0 transition-colors hover:bg-surface/60"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-ink-muted tabular-nums">
                      {new Date(item.createdAt).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="urgent" size="sm">
                        {ACTION_LABEL[item.action] ?? item.action}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-ink-muted">{item.actorEmail}</td>
                    <td className="px-4 py-3 text-ink">
                      {item.targetTenantNom ?? <span className="text-ink-muted">—</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-muted tabular-nums">
                      {item.ipAddress ?? '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3 text-sm text-ink-muted">
            <span className="tabular-nums">
              Page {page} sur {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Page précédente"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Page suivante"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}
